import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	deleteSecurityRatcheting,
	getSecurityRatcheting,
	updateSecurityRatcheting,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import {
	makeBooleanDropdownOptions,
	SecurityFeatureBaseProps,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import {
	mapFormDataToBody,
	mapScheduleResToFormData,
	RatchetingData,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/data.mock.ts';
import { RatchetingFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/ratcheting.zod.ts';
import { RatchetScheduleEdit } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/RatchetScheduleEdit/RatchetScheduleEdit.tsx';
import { RatchetScheduleView } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/RatchetScheduleView/RatchetScheduleView.tsx';
import { createSingleFlight } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/singleFlight.ts';
import {
	getRatchetingMetricLabel,
	getRatchetingMetricOptions,
	RATCHETING_FORM_FIELD,
	useRatchetingController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/Ratcheting/useRatchetingController.ts';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { FormSegmentedControl } from '@dealstack/frontend/shared/components/FormSegmentedControl/FormSegmentedControl.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import {
	SelectedSubjectBadge,
	SelectedSubjectsList,
} from '@dealstack/frontend/shared/components/SelectedSubjectsList/SelectedSubjectsList.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { Flex, Text } from '@mantine/core';
import { MultiSelect } from 'react-hook-form-mantine';

const RATCHET_QUERY_KEY = (securityId: string) => [
	formatUnicorn(API_PATHS.SECURITY_RATCHET_SECURITYID_GET, { securityId }),
];

interface Props extends SecurityFeatureBaseProps {
	isNewFeature?: boolean;
}

export const Ratcheting = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
	isNewFeature = false,
}: PropsWithChildren<Props>) => {
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const queryClient = useQueryClient();

	const securityId = security?.id;

	const { data: scheduleRes, isLoading: isQueryLoading } = useQuery({
		queryKey: RATCHET_QUERY_KEY(securityId ?? ''),
		queryFn: () => getSecurityRatcheting(securityId!),
		enabled: !!securityId,
	});

	const ratchetingData: RatchetingData | null = useMemo(
		() => (scheduleRes ? mapScheduleResToFormData(scheduleRes) : null),
		[scheduleRes]
	);

	const {
		ratchetingForm,
		dealPersonOptions,
		scheduleFieldArray,
		addScheduleItem,
		removeScheduleItem,
		resetForm,
	} = useRatchetingController(ratchetingData);
	const metricType = ratchetingForm.watch(RATCHETING_FORM_FIELD.metric);
	const booleanOptions = useMemo(() => makeBooleanDropdownOptions(tCommonUi), [tCommonUi]);

	const [isEdit, setIsEdit] = useState(isNewFeature);

	useEffect(() => {
		if (isNewFeature) {
			setIsEdit(true);
		}
	}, [isNewFeature]);

	const [updateGuard] = useState(createSingleFlight);

	const updateRatchetingRequest = useMutation({
		mutationFn: updateSecurityRatcheting,
		onSettled: () => updateGuard.finish(),
	});
	const deleteRatchetingRequest = useMutation({
		mutationFn: deleteSecurityRatcheting,
	});

	const isLoading =
		isQueryLoading || updateRatchetingRequest.isPending || deleteRatchetingRequest.isPending;

	// If no existing data and not adding new, don't render
	if (!isQueryLoading && !scheduleRes && !isNewFeature) {
		return null;
	}

	const handleUpdateRatcheting = (args: RatchetingFormDto) => {
		if (!securityId) {
			throw new Error('Expect securityId to be defined');
		}

		const body = mapFormDataToBody(args, args.initialEntitlement);
		if (!updateGuard.tryStart()) {
			return;
		}

		updateRatchetingRequest.mutate(
			{ securityId, body },
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.ratcheting_updated_successfully'),
					});

					void queryClient.invalidateQueries({
						queryKey: RATCHET_QUERY_KEY(securityId),
					});
					refreshDetails?.(securityId);
					setIsEdit(false);
				},
			}
		);
	};
	const handleDeleteRatcheting = () => {
		if (!securityId) {
			throw new Error('Expect securityId to be defined');
		}

		deleteRatchetingRequest.mutate(securityId, {
			onSuccess: () => {
				showToastV2({
					title: tCapManage('toasts.success.ratcheting_deleted_successfully'),
				});
				void queryClient.invalidateQueries({
					queryKey: RATCHET_QUERY_KEY(securityId),
				});
				refreshDetails?.(securityId);
				setIsEdit(false);
			},
		});
	};

	const handleCancel = () => {
		resetForm();
		onCancel?.();
	};

	const resolveSubjectName = (personId: string) => {
		const option = dealPersonOptions.find((o) => o.value === personId);
		return option?.label ?? personId;
	};

	return (
		<EditableInfoContainer
			title={tCapManage('securities.ratcheting.title')}
			isEdit={isEdit}
			setIsEdit={setIsEdit}
			disableButtons={isLoading}
			onUpdate={ratchetingForm.handleSubmit(handleUpdateRatcheting)}
			onRemove={handleDeleteRatcheting}
			onCancel={handleCancel}
			hideRemove={!scheduleRes}
			hasEditorPermission={hasEditorPermission}
			updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON}
			editBtnDataTestId={TEST_ELEMENT_IDS.CAP_RATCHETING_EDIT_BUTTON}
		>
			<FormProvider {...ratchetingForm}>
				{isLoading && <InlineLoader />}
				<form
					onSubmit={ratchetingForm.handleSubmit(handleUpdateRatcheting)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid
						$gridColumns={3}
						$gridTemplateColumns="1fr 1fr 2fr"
						$alignItems="start"
					>
						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.ratcheting.metric.label')}
							data={getRatchetingMetricLabel(tCommonUi, ratchetingData?.metric)}
							formInput={
								<FormSegmentedControl
									name={RATCHETING_FORM_FIELD.metric}
									data={getRatchetingMetricOptions(
										tCommonUi,
										ratchetingData?.metric
									)}
									mt="0.25rem"
								/>
							}
						/>

						<EditableInfoField
							editMode={isEdit}
							label={tCapManage('securities.ratcheting.subjects.label')}
							formInput={
								<Flex direction="column" gap="xs">
									<MultiSelect
										name={RATCHETING_FORM_FIELD.subjects}
										control={ratchetingForm.control}
										data={dealPersonOptions}
										searchable
										clearable
										placeholder={tCapManage(
											'securities.ratcheting.subjects.placeholder'
										)}
										leftSection={<Icon variant="search" size="lg" />}
										styles={{
											input: { paddingLeft: '1.5rem !important' },
										}}
									/>

									<SelectedSubjectsList
										selectedIds={ratchetingForm.watch(
											RATCHETING_FORM_FIELD.subjects
										)}
										options={dealPersonOptions}
										onRemove={(id) => {
											const current = ratchetingForm.getValues(
												RATCHETING_FORM_FIELD.subjects
											);
											ratchetingForm.setValue(
												RATCHETING_FORM_FIELD.subjects,
												current.filter((v) => v !== id)
											);
										}}
									/>
								</Flex>
							}
							customValueElement={
								ratchetingData?.subjects?.length ? (
									<Flex gap="0.5rem" wrap="wrap" mt="0.25rem">
										{ratchetingData.subjects.map((personId) => (
											<SelectedSubjectBadge
												key={personId}
												id={personId}
												name={resolveSubjectName(personId)}
											/>
										))}
									</Flex>
								) : (
									<p>{LONG_DASH}</p>
								)
							}
						/>

						<EditableInfoField
							editMode={isEdit}
							label={tCapManage('securities.ratcheting.ratchet_schedule.label')}
							formInput={
								<RatchetScheduleEdit
									scheduleFieldArray={scheduleFieldArray}
									metricType={metricType}
									addScheduleItem={addScheduleItem}
									removeScheduleItem={removeScheduleItem}
								/>
							}
							customValueElement={
								ratchetingData?.schedule ? (
									<RatchetScheduleView
										schedule={ratchetingData.schedule}
										metricType={ratchetingData.metric}
									/>
								) : undefined
							}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage(
								'securities.ratcheting.distribute_until_breakpoint.label'
							)}
							data={
								ratchetingData?.distributeUntilBreakpoint
									? tCommonUi('boolean.true')
									: tCommonUi('boolean.false')
							}
							valueDataTestId={
								TEST_ELEMENT_IDS.CAP_RATCHETING_DISTRIBUTE_UNTIL_BREAKPOINT_VALUE
							}
							formInput={
								<Flex direction="column" gap="0.25rem">
									<FormSegmentedControl
										name={RATCHETING_FORM_FIELD.distributeUntilBreakpoint}
										handleAsBoolean
										data={booleanOptions}
										data-testid={
											TEST_ELEMENT_IDS.CAP_RATCHETING_DISTRIBUTE_UNTIL_BREAKPOINT_TOGGLE
										}
										mt="0.25rem"
									/>
									<Text size="xs" c="dark.0">
										{tCapManage(
											'securities.ratcheting.distribute_until_breakpoint.helper'
										)}
									</Text>
								</Flex>
							}
						/>
					</EditableInfoContainerGrid>
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
